import React, { useState } from 'react';
import classNames from 'classnames';
import { Controller } from 'react-hook-form';
import type {
  Control,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormClearErrors,
  UseFormSetValue,
  FieldValues,
  FieldArrayWithId,
} from 'react-hook-form';

import Select from 'components/partials/select/select';
import RadioInput from 'components/partials/radio-input/radio-input';
import Textarea from 'components/partials/textarea/textarea';
import Button from 'components/partials/button/button';
import CriticalElementDetails from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-element-details/critical-element-details';
import GeneratingTextarea from 'components/partials/generating-textarea/generating-textarea';

import {
  RADIO_OPTIONS,
  METRIC_OPTIONS,
  STAGE_OPTIONS,
  SELECT_CUSTOM_STYLES,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-elements-form.const';

import Icons from 'assets/icons';

interface CriticalElementFormViewProps {
  detailFields: FieldArrayWithId<FieldValues, 'details', 'id'>[];
  append: UseFieldArrayAppend<FieldValues, 'details'>;
  remove: UseFieldArrayRemove;
  isDisabled: boolean;
  control: Control<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  generateSummary: () => Promise<void>;
  generationLoading: boolean;
  initialShowNote: boolean;
  setValue: UseFormSetValue<FieldValues>;
}

const CriticalElementFormView = ({
  detailFields,
  append,
  remove,
  isDisabled,
  control,
  clearErrors,
  generateSummary,
  generationLoading,

  initialShowNote,
  setValue,
}: CriticalElementFormViewProps): React.JSX.Element => {
  const [showNote, setShowNote] = useState<boolean>(initialShowNote);

  const handleDeleteNote = (): void => {
    setValue('note', '');
    setShowNote(false);
  };

  const renderDetailFields = (): React.JSX.Element => (
    <>
      {detailFields.map((item, detailIndex) => (
        <div
          key={item.id}
          className={classNames({
            'flex gap-3 justify-start items-start w-full mb-3': true,
          })}
        >
          <CriticalElementDetails
            detailIndex={detailIndex}
            detailData={item}
            isDisabled={isDisabled}
            onRemove={
              detailFields.length > 1 ? () => remove(detailIndex) : undefined
            }
          />
        </div>
      ))}
      {!isDisabled && (
        <Button
          type="button"
          onClick={() =>
            append({ category: '', focusArea: '', subArea: '', options: [] })
          }
          variant="reserve-outlined"
          iconAfter={<Icons.PlusInCircle />}
          size="sm"
          className="mt-3"
        >
          and
        </Button>
      )}
    </>
  );

  return (
    <form>
      <div className="mb-7">
        <p className="font-semibold text-gray-700 leading-5 mb-2.5">
          Key population
        </p>
        <div>{renderDetailFields()}</div>
      </div>

      <div className="mb-7">
        <p className="font-semibold text-gray-700 mb-2.5 leading-5">
          Focus metric
        </p>
        <div className="flex gap-3 w-full flex-wrap">
          <Controller
            name="stage"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                value={STAGE_OPTIONS.find((opt) => opt.value === value)}
                onChange={(opt) => {
                  if (opt && !Array.isArray(opt)) {
                    onChange(opt.value);
                  }
                }}
                options={STAGE_OPTIONS}
                disabled={isDisabled}
                selectWrapperClass="max-w-[150px]"
                labelInTop
                label="Admission funnel"
                fieldError={error && { ...error, message: 'Required' }}
                isClearable={false}
                clearInputErr={() => clearErrors('stage')}
                required
                {...SELECT_CUSTOM_STYLES}
              />
            )}
          />

          <Controller
            name="metric"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                value={METRIC_OPTIONS.find((opt) => opt.value === value)}
                onChange={(opt) => {
                  if (opt && !Array.isArray(opt)) {
                    onChange(opt.value);
                  }
                }}
                options={METRIC_OPTIONS}
                disabled={isDisabled}
                selectWrapperClass="max-w-[150px]"
                labelInTop
                label="Metric type"
                fieldError={error && { ...error, message: 'Required' }}
                isClearable={false}
                clearInputErr={() => clearErrors('metric')}
                required
                {...SELECT_CUSTOM_STYLES}
              />
            )}
          />
        </div>

        <div className="mt-4 flex">
          <Controller
            name="relativeToLastYear"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                {RADIO_OPTIONS.map((opt) => (
                  <RadioInput
                    key={opt.value}
                    labelFor={opt.value}
                    label={opt.label}
                    radioValue={opt.value}
                    labelClass={`${opt.colorClass} font-semibold`}
                    setAction={field.onChange}
                    disabled={isDisabled}
                    {...field}
                  />
                ))}
              </>
            )}
          />
        </div>
      </div>

      <div className="mb-6">
        <Controller
          name="summary"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <GeneratingTextarea
              {...field}
              label="AI Description"
              labelInTop
              loading={generationLoading}
              disabled={isDisabled}
              errorsNames={error}
              textareaWrapperStyles="w-[550px]"
              helperText="The AI Description will be used on the Client's Goals page."
              required
              rows={5}
              data-testid="critical-element-summary-textarea"
            />
          )}
        />
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="reserve-outlined"
            onClick={generateSummary}
            disabled={isDisabled || generationLoading}
            size="sm"
            iconBefore={<Icons.Ai />}
            type="button"
          >
            Generate description
          </Button>
        </div>
      </div>

      {!showNote ? (
        <div className="mt-5">
          <Button
            variant="reserve-outlined"
            onClick={() => setShowNote(true)}
            disabled={isDisabled}
            size="sm"
            iconBefore={<Icons.Settings />}
            type="button"
          >
            Add note
          </Button>
        </div>
      ) : (
        <div className="mt-5">
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <div className="relative">
                <Textarea
                  {...field}
                  label="Note"
                  placeholder="Use this area to leave a note..."
                  disabled={isDisabled}
                  rows={5}
                />
                <div className="absolute top-0 right-0">
                  <Button
                    type="button"
                    onClick={handleDeleteNote}
                    disabled={isDisabled}
                    variant="reserve-text"
                    className="text-navy-300"
                    iconBefore={<Icons.Delete />}
                    size="sm"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </form>
  );
};

export default CriticalElementFormView;
